import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../../hooks'
import {
  type EnrollmentJointDocumentVerificationData,
  type EnrollmentJointDocumentVerificationSubmitInput,
  type EnrollmentJointDocumentVerificationSubmitOutput,
  type EnrollmentJointDocVerificationCheckStatusInput,
} from '../../../../../models/enrollment/joint/document-verification'

/**
 * Get Enrollment Joint DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationData>
 * @example const response = await getEnrollmentJointDocumentVerificationData()
 */
export const getEnrollmentJointDocumentVerificationData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointDocumentVerificationData',
      query: `mutation getEnrollmentJointDocumentVerificationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            progress
            applicationId
            documentVerificationStatus
            documentVerificationUrl
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointDocumentVerificationData = createQueryHook<
  undefined,
  EnrollmentJointDocumentVerificationData
>(getEnrollmentJointDocumentVerificationData)

/**
 * Submit Enrollment Joint DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentJointDocumentVerificationData();
 */
export const submitEnrollmentJointDocumentVerificationData = async (
  enrollmentDocumentVerificationSubmitInput: EnrollmentJointDocumentVerificationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDocumentVerificationData',
      query: `mutation submitEnrollmentJointDocumentVerificationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            documentVerificationUrl
            documentVerificationStatus
          }
        }
      }`,
    },
    {
      input: enrollmentDocumentVerificationSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDocumentVerificationData = createMutationHook<
  EnrollmentJointDocumentVerificationSubmitInput,
  EnrollmentJointDocumentVerificationSubmitOutput
>(submitEnrollmentJointDocumentVerificationData)

/**
 * Submit Enrollment Joint DocumentVerificationCheckStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentJointDocVerificationCheckStatusData();
 */
export const submitEnrollmentJointDocVerificationCheckStatusData = async (
  enrollmentDocVerificationCheckStatusInput: EnrollmentJointDocVerificationCheckStatusInput,
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDocVerificationCheckStatusData',
      query: `mutation submitEnrollmentJointDocVerificationCheckStatusData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            documentVerificationStatus
          }
        }
      }`,
    },
    {
      input: enrollmentDocVerificationCheckStatusInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDocVerificationCheckStatusData = createMutationHook<
  EnrollmentJointDocVerificationCheckStatusInput,
  EnrollmentJointDocumentVerificationSubmitOutput
>(submitEnrollmentJointDocVerificationCheckStatusData)
